import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AtlasProvider } from "./context/AtlasContext";
import { BillingProvider, useBilling } from "./context/BillingContext";
import { Toaster } from "./components/ui/sonner";
import { useEffect, lazy, Suspense } from "react";
import { openCalendly } from "./lib/calendly";

// ---- Eager: HomePage stays in the main bundle so first paint has no split ----
import HomePage from "./pages/HomePage";
import ErrorBoundary from "./components/ErrorBoundary";
import RequirementDetailPage from './pages/RequirementDetailPage';
import PWAInstallBanner from "./components/PWAInstallBanner";
import OnboardingOverlay from "./components/OnboardingOverlay";
import AtlasFloatingButton from "./components/AtlasFloatingButton";

// ---- Lazy-loaded: every other page ships as its own chunk ----
const LoginPage                  = lazy(() => import("./pages/LoginPage"));
const RegisterPage               = lazy(() => import("./pages/RegisterPage"));
const PrivacyPolicyPage          = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage         = lazy(() => import("./pages/TermsOfServicePage"));
const TrustCenterPage            = lazy(() => import("./pages/TrustCenterPage"));
const ForgotPasswordPage         = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage          = lazy(() => import("./pages/ResetPasswordPage"));
const OnboardingPage             = lazy(() => import("./pages/OnboardingPage"));
const CCGOnboardingPage          = lazy(() => import("./pages/CCGOnboardingPage"));
const CommandCenterPage          = lazy(() => import("./pages/CommandCenterPage"));
const MissionsListPage           = lazy(() => import("./pages/MissionsListPage"));
const MissionWorkspacePage       = lazy(() => import("./pages/MissionWorkspacePage"));
const PricingWarRoomPage         = lazy(() => import("./pages/PricingWarRoomPage"));
const SettingsPage               = lazy(() => import("./pages/SettingsPage"));
const MyProfilePage              = lazy(() => import("./pages/MyProfilePage"));
const CompanyIntelligencePage    = lazy(() => import("./pages/CompanyIntelligencePage"));
const OpportunitiesPage          = lazy(() => import("./pages/OpportunitiesPage"));
const PredictiveSignalsPage      = lazy(() => import("./pages/PredictiveSignalsPage"));
const IntelligencePage           = lazy(() => import("./pages/IntelligencePage"));
const SignalDetailPage           = lazy(() => import("./pages/SignalDetailPage"));
const AmendmentIntelligencePage  = lazy(() => import("./pages/AmendmentIntelligencePage"));
const ProposalLibraryPage        = lazy(() => import("./pages/ProposalLibraryPage"));
const ProposalReviewPage         = lazy(() => import("./pages/ProposalReviewPage"));
const NotificationsPage          = lazy(() => import("./pages/NotificationsPage"));
const IntegrationsPage           = lazy(() => import("./pages/IntegrationsPage"));
const CompliancePage             = lazy(() => import("./pages/CompliancePage"));
const PostAwardPage              = lazy(() => import("./pages/PostAwardPage"));
const LessonsLearnedPage         = lazy(() => import("./pages/LessonsLearnedPage"));
const AgentsConsolePage          = lazy(() => import("./pages/AgentsConsolePage"));
const ConversionTrackerPage      = lazy(() => import("./pages/ConversionTrackerPage"));
const SignalDetailPage2          = lazy(() => import("./pages/SignalDetailPage2"));
const TeamingPartnersPage        = lazy(() => import("./pages/TeamingPartnersPage"));
const TeamingWorkspacesListPage  = lazy(() => import("./pages/TeamingWorkspacesListPage"));
const TeamingWorkspacePage       = lazy(() => import("./pages/TeamingWorkspacePage"));
const TeamingInvitationPage      = lazy(() => import("./pages/TeamingInvitationPage"));
const SigningPage                = lazy(() => import("./pages/SigningPage"));
const ContactsPage               = lazy(() => import("./pages/ContactsPage"));
const ContactDetailPage          = lazy(() => import("./pages/ContactDetailPage"));
const AgenciesPage               = lazy(() => import("./pages/AgenciesPage"));
const BDTasksPage                = lazy(() => import("./pages/BDTasksPage"));
const ReportsPage                = lazy(() => import("./pages/ReportsPage"));
const SalesIntelligencePage      = lazy(() => import("./pages/SalesIntelligencePage"));
const AdminInfraHealthPage       = lazy(() => import("./pages/AdminInfraHealthPage"));
const AtlasPage                 = lazy(() => import("./pages/AtlasChatPage"));
const AtlasMorningBriefingPage  = lazy(() => import("./pages/AtlasMorningBriefingPage"));
const AtlasAuditLogPage         = lazy(() => import("./pages/AtlasAuditLogPage"));
const AtlasConnectorsPage       = lazy(() => import("./pages/AtlasConnectorsPage"));
const WebinarsPage               = lazy(() => import("./pages/Webinars"));
const LostContractsPage          = lazy(() => import("./pages/LostContractsPage"));
const RecompetePage              = lazy(() => import("./pages/RecompetePage"));
const BillingPage                = lazy(() => import("./pages/BillingPage"));
const RequestQuotePage           = lazy(() => import("./pages/RequestQuotePage"));
const TrialExpiredPage           = lazy(() => import("./pages/TrialExpiredPage"));
const WorkflowStudioPage         = lazy(() => import("./pages/WorkflowStudioPage"));

// Silence unused-import warning for OnboardingPage kept for backwards-compat exports
void OnboardingPage;

const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: '#F5F1EC' }}
    data-testid="route-suspense-fallback"
  >
    <div className="w-8 h-8 border-2 border-slate-300 border-t-[#C49A00] rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { subscription, loading: billingLoading } = useBilling();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (billingLoading && !subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (subscription?.quote_trial_enabled && subscription?.needs_plan_selection) {
    return <Navigate to="/request-quote" replace />;
  }

  if (subscription?.quote_trial_enabled && subscription?.quote_trial_flow_version === 'quote_v1' && !subscription?.quote_intro_completed) {
    return <Navigate to="/request-quote" replace />;
  }

  if (subscription?.quote_trial_enabled && subscription?.expired) {
    return <Navigate to="/trial-expired" replace />;
  }

  return children;
};

const InternalOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_bsiq_internal && !user?.is_demo_mode) {
    return <Navigate to="/command-center" replace />;
  }

  return children;
};

const RoleRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const role = user.role || 'admin';
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/command-center" replace />;
  }

  return children;
};

const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.has_ccg ? "/command-center" : "/onboarding"} replace />;
  }

  return children;
};

const DemoRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const tryOpen = () => {
      if (cancelled) return;
      attempts += 1;
      if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
        openCalendly();
        navigate('/', { replace: true });
      } else if (attempts > 25) {
        openCalendly();
        navigate('/', { replace: true });
      } else {
        setTimeout(tryOpen, 200);
      }
    };
    tryOpen();
    return () => { cancelled = true; };
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F1EC' }}>
      <div className="w-8 h-8 border-2 border-slate-300 border-t-[#C49A00] rounded-full animate-spin" />
    </div>
  );
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<Navigate to="/#features" replace />} />
      <Route path="/pricing" element={<ProtectedRoute><PricingWarRoomPage /></ProtectedRoute>} />
      <Route path="/case-studies" element={<Navigate to="/" replace />} />
      <Route path="/demo" element={<DemoRedirect />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/select-plan" element={<Navigate to="/request-quote" replace />} />
      <Route path="/request-quote" element={<RoleRoute allowedRoles={['admin']}><RequestQuotePage /></RoleRoute>} />
      <Route path="/trial-expired" element={<RoleRoute allowedRoles={['admin']}><TrialExpiredPage /></RoleRoute>} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/trust-center" element={<TrustCenterPage />} />
      <Route path="/sign/:token" element={<SigningPage />} />
      <Route path="/teaming/invite/:token" element={<TeamingInvitationPage />} />

      <Route path="/onboarding" element={<ProtectedRoute><CCGOnboardingPage /></ProtectedRoute>} />
      <Route path="/onboarding/step-1" element={<ProtectedRoute><CCGOnboardingPage /></ProtectedRoute>} />
      <Route path="/onboarding/review" element={<ProtectedRoute><CCGOnboardingPage /></ProtectedRoute>} />
      <Route path="/command-center" element={<ProtectedRoute><CommandCenterPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><CommandCenterPage /></ProtectedRoute>} />
      <Route path="/discover" element={<Navigate to="/missions" replace />} />
      <Route path="/missions" element={<ProtectedRoute><MissionsListPage /></ProtectedRoute>} />
      <Route path="/missions/:id" element={<ProtectedRoute><MissionWorkspacePage /></ProtectedRoute>} />
      <Route path="/missions/:id/pricing" element={<RoleRoute allowedRoles={['admin', 'capture_manager']}><PricingWarRoomPage /></RoleRoute>} />
      <Route path="/pricing-war-room" element={<RoleRoute allowedRoles={['admin', 'capture_manager']}><PricingWarRoomPage /></RoleRoute>} />
      <Route path="/pricing-war-room/:id" element={<RoleRoute allowedRoles={['admin', 'capture_manager']}><PricingWarRoomPage /></RoleRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin/sales" element={<InternalOnlyRoute><SalesIntelligencePage /></InternalOnlyRoute>} />
      <Route path="/admin/infra" element={<InternalOnlyRoute><AdminInfraHealthPage /></InternalOnlyRoute>} />
      <Route path="/atlas" element={<ProtectedRoute><AtlasPage /></ProtectedRoute>} />
      <Route path="/morning" element={<ProtectedRoute><AtlasMorningBriefingPage /></ProtectedRoute>} />
      <Route path="/atlas/briefing" element={<ProtectedRoute><AtlasMorningBriefingPage /></ProtectedRoute>} />
      <Route path="/atlas/approvals" element={<Navigate to="/atlas?tab=approvals" replace />} />
      <Route path="/settings/atlas/autonomy" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/atlas/audit-log" element={<ProtectedRoute><AtlasAuditLogPage /></ProtectedRoute>} />
      <Route path="/settings/atlas/connectors" element={<ProtectedRoute><AtlasConnectorsPage /></ProtectedRoute>} />
      <Route path="/post-award" element={<ProtectedRoute><PostAwardPage /></ProtectedRoute>} />
      <Route path="/post-award/:piid" element={<ProtectedRoute><PostAwardPage /></ProtectedRoute>} />
      <Route path="/lost-contracts" element={<ProtectedRoute><LostContractsPage /></ProtectedRoute>} />
      <Route path="/recompete" element={<ProtectedRoute><RecompetePage /></ProtectedRoute>} />
      <Route path="/lessons" element={<ProtectedRoute><LessonsLearnedPage /></ProtectedRoute>} />
      <Route path="/agents-console" element={<ProtectedRoute><AgentsConsolePage /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute><AgentsConsolePage /></ProtectedRoute>} />
      <Route path="/teaming" element={<Navigate to="/teaming-partners" replace />} />
      <Route path="/teaming-partners" element={<ProtectedRoute><TeamingPartnersPage /></ProtectedRoute>} />
      <Route path="/teaming/workspaces" element={<ProtectedRoute><TeamingWorkspacesListPage /></ProtectedRoute>} />
      <Route path="/teaming/workspace/:workspaceId" element={<ProtectedRoute><TeamingWorkspacePage /></ProtectedRoute>} />
      <Route path="/partners" element={<Navigate to="/teaming-partners" replace />} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/contacts/:id" element={<ProtectedRoute><ContactDetailPage /></ProtectedRoute>} />
      <Route path="/agencies" element={<ProtectedRoute><AgenciesPage /></ProtectedRoute>} />
      <Route path="/intelligence/contracting-officers" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/intelligence/contractors" element={<ProtectedRoute><TeamingPartnersPage /></ProtectedRoute>} />
      <Route path="/bd-tasks" element={<ProtectedRoute><BDTasksPage /></ProtectedRoute>} />
      <Route path="/webinars" element={<ProtectedRoute><WebinarsPage /></ProtectedRoute>} />
      <Route path="/opportunity/:id" element={<ProtectedRoute><MissionWorkspacePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/billing" element={<RoleRoute allowedRoles={['admin']}><BillingPage /></RoleRoute>} />
      <Route path="/settings/workflows" element={<ProtectedRoute><WorkflowStudioPage /></ProtectedRoute>} />
      <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesPage /></ProtectedRoute>} />
      <Route path="/signals" element={<ProtectedRoute><PredictiveSignalsPage /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><ErrorBoundary label="Intelligence"><IntelligencePage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/amendments" element={<ProtectedRoute><ErrorBoundary label="Amendment Intelligence"><AmendmentIntelligencePage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/conversion-tracker" element={<ProtectedRoute><ConversionTrackerPage /></ProtectedRoute>} />
      <Route path="/conversion-tracker/:signalId" element={<ProtectedRoute><SignalDetailPage2 /></ProtectedRoute>} />
      <Route path="/signal/:id" element={<ProtectedRoute><SignalDetailPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute><CompanyIntelligencePage /></ProtectedRoute>} />
      <Route path="/proposal-dna" element={<Navigate to="/company" replace />} />
      <Route path="/past-performance" element={<Navigate to="/company" replace />} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/proposal-library" element={<ProtectedRoute><ProposalLibraryPage /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute><ProposalReviewPage /></ProtectedRoute>} />
      <Route path="/requirement/:id" element={<ProtectedRoute><RequirementDetailPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

const MarketingRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<Navigate to="/#features" replace />} />
      <Route path="/case-studies" element={<Navigate to="/" replace />} />
      <Route path="/demo" element={<DemoRedirect />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/trust-center" element={<TrustCenterPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);

function App() {
  const marketingOnly = process.env.REACT_APP_MARKETING_ONLY === 'true';

  if (marketingOnly) {
    return (
      <BrowserRouter>
        <ThemeProvider>
          <MarketingRoutes />
        </ThemeProvider>
      </BrowserRouter>
    );
  }

  return (
    <>
      <PWAInstallBanner />
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <BillingProvider>
              <AtlasProvider>
                <AppRoutes />
                <OnboardingOverlay />
                <AtlasFloatingButton />
                <Toaster position="top-right" richColors />
              </AtlasProvider>
            </BillingProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
